"use client";

import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { cn } from "@/lib/utils";

gsap.registerPlugin(ScrollTrigger);

interface AnimateOnScrollProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}

export default function AnimateOnScroll({
  children,
  className,
  delay = 0,
}: AnimateOnScrollProps) {
  const ref = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const els = Array.from(ref.current?.children ?? []) as HTMLElement[];
      if (!els.length) return;

      els.forEach((el) => {
        if (el.dataset.fromLeft !== undefined) {
          gsap.set(el, { x: -48, opacity: 0 });
        } else {
          gsap.set(el, { y: 32, opacity: 0 });
        }
      });

      let hasPlayed = false;

      const playAnimation = () => {
        if (hasPlayed) return;
        hasPlayed = true;

        const tl = gsap.timeline({ delay });
        els.forEach((el, i) => {
          if (el.dataset.fromLeft !== undefined) {
            tl.to(el, { x: 0, opacity: 1, duration: 0.6, ease: "power2.out" }, i * 0.1);
          } else {
            tl.to(el, { y: 0, opacity: 1, duration: 0.65, ease: "power2.out" }, i * 0.1);
          }
        });
      };

      ScrollTrigger.create({
        trigger: ref.current,
        start: "top 88%",
        once: true,
        onEnter: playAnimation,
      });

      const checkAlreadyInView = () => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.88) {
          playAnimation();
        }
      };

      checkAlreadyInView();
      ScrollTrigger.addEventListener("refresh", checkAlreadyInView);

      return () => {
        ScrollTrigger.removeEventListener("refresh", checkAlreadyInView);
      };
    },
    { scope: ref },
  );

  return (
    <div ref={ref} className={cn(className)}>
      {children}
    </div>
  );
}
